import React, { Component } from "react";
import { Button, Drawer, Form, Input, Select, Steps, Switch, message } from "antd";
import API from "../../API/API";
import { PlusOutlined } from "@ant-design/icons";
import { useDomainGroups } from "../../Context/DomainGroupsContext";

const { Step } = Steps;
const { Option } = Select;

// The form field backing one group's switch.
const groupFieldName = (group) => `domainGroup_${group._id}`;

/**
 * The legacy booleans are still written alongside the memberships. They are what
 * the backend falls back to for an agent carrying no domainGroups, so keeping
 * them in step means a failed membership call cannot leave a new agent
 * receiving nothing.
 */
const legacyFlagsForGroups = (role, subRole, selectedGroups) => {
  if (role !== "SALES-AGENT" || subRole !== "TIER-1") {
    return { subset1: false, subset2: false, premium: false };
  }

  const keys = selectedGroups.map((group) => group.key);

  return {
    subset1: keys.includes("subset1"),
    subset2: keys.includes("subset2"),
    premium: keys.includes("premium"),
  };
};

class AddTeam extends Component {
  /** The visible groups of the tier currently selected in the form. */
  getTierGroups = () => {
    const { selectedRole, selectedSubRole } = this.state;

    if (selectedRole !== "SALES-AGENT" || !selectedSubRole) {
      return [];
    }

    return (this.props.domainGroups || []).filter(
      (group) => group.visible !== false && group.tier === selectedSubRole
    );
  };

  /** Every group switch set back to off, for a role or tier change. */
  clearedGroupFields = () => (this.props.domainGroups || []).reduce((fields, group) => {
    fields[groupFieldName(group)] = false;
    return fields;
  }, {});

  constructor(props) {
    super(props);
    this.state = {
      visible: false,
      currentStep: 0,
      formValues: {},
      loading: false,
      selectedRole: "",
      selectedSubRole: "",
    };
    this.formRef = React.createRef();
  }

  showDrawer = () => {
    this.setState({ visible: true });
  };

  closeDrawer = () => {
    this.setState({
      visible: false,
      currentStep: 0,
      formValues: {},
      loading: false,
      selectedRole: "",
      selectedSubRole: "",
    });
    this.formRef.current.resetFields();
  };

  formatPhoneNumber = (value) => {
    let cleaned = value.replace(/\D/g, "").slice(0, 11);
    if (cleaned.startsWith("1")) {
      cleaned = cleaned.slice(1);
    }
    let formattedNumber = "+1";
    if (cleaned.length > 0) formattedNumber += ` (${cleaned.slice(0, 3)}`;
    if (cleaned.length >= 4) formattedNumber += `) ${cleaned.slice(3, 6)}`;
    if (cleaned.length >= 7) formattedNumber += `-${cleaned.slice(6)}`;
    return formattedNumber;
  };

  validatePhoneNumber = (_, value) => {
    if (!value) return Promise.reject("Enter a valid 10-digit US phone number.");
    const cleaned = value.replace(/\D/g, "");
    return cleaned.length === 11 ? Promise.resolve() : Promise.reject("Enter a valid 10-digit US phone number.");
  };

  handleNameChange = (field, e) => {
    let value = e.target.value;
    value = value.replace(/\b\w/g, (char) => char.toUpperCase());
    this.formRef.current.setFieldsValue({ [field]: value });

    const form = this.formRef.current.getFieldsValue();

    // ✅ Always derive latest values safely
    const first =
      field === "firstName" ? value : (form.firstName || "");
    const last =
      field === "lastName" ? value : (form.lastName || "");

    // ✅ If either field becomes completely empty → clear email
    if (!first.trim() || !last.trim()) {
      this.formRef.current.setFieldsValue({ email: "" });
      return;
    }

    // 🔒 YOUR EXISTING CODE — NOT REMOVED
    if (first && last) {
      const userName =
        first.charAt(0).toUpperCase() + last.replace(/\s+/g, "");

      // ✅ EMAIL AUTO-GENERATION (live update)
      const email =
        first
          .trim()
          .charAt(0)
          .toLowerCase() +
        last
          .trim()
          .replace(/\s+/g, "")
          .toLowerCase() +
        "@premiumusedautoparts.com";

      this.formRef.current.setFieldsValue({
        userName,
        email,
      });
    }
  };

  validateExtension = (_, value) => {
    if (!/^\d{1,3}$/.test(value)) {
      return Promise.reject("Extension must be up to 3 digits.");
    }
    return Promise.resolve();
  };

  validateUsername = (_, value) => {
    if (!value) return Promise.resolve();
    if (!/^[a-zA-Z0-9@._-]+$/.test(value)) {
      return Promise.reject("Username can only contain letters, numbers, and @ . _ -.");
    }
    return Promise.resolve();
  };

  nextStep = () => {
    this.formRef.current
      .validateFields()
      .then((values) => {
        this.setState((prevState) => ({
          formValues: { ...prevState.formValues, ...values },
          currentStep: prevState.currentStep + 1,
        }));
      })
      .catch(() => console.log("Please fill required fields correctly."));
  };

  prevStep = () => {
    this.setState((prevState) => ({ currentStep: prevState.currentStep - 1 }));
  };

  onFinish = async () => {
    this.setState({ loading: true });
    try {
      await this.formRef.current.validateFields();
      const latestValues = this.formRef.current.getFieldsValue();
      const allValues = { ...this.state.formValues, ...latestValues };

      const payload = {
        firstName: allValues.firstName.trim(),
        lastName: allValues.lastName.trim(),
        email: allValues.email,
        role: allValues.role,
        extension: allValues.extension,
        mailboxId: allValues.extension, // set mailboxId from extension
        directNumber: allValues.directNumber,
        userName: allValues.userName,
        rcStatus: allValues.rcStatus || 'Unknown',
        rcTelephonyStatus: allValues.rcTelephonyStatus || 'Unknown',
        rcLastActive: allValues.rcLastActive || new Date().toISOString(), // default to current date-time
        rcExtensionId: allValues.rcExtensionId || allValues.extension
      };

      const selectedGroups = this.getTierGroups().filter(
        (group) => allValues[groupFieldName(group)] === true
      );

      const subsetFlags = legacyFlagsForGroups(
        allValues.role,
        allValues.subRole,
        selectedGroups
      );

      if (allValues.role !== "ADMIN") {
        payload.subRole = allValues.subRole;
      }

      payload.subset1 = subsetFlags.subset1;
      payload.subset2 = subsetFlags.subset2;
      payload.premium = subsetFlags.premium;

      // register() takes the memberships and writes them with the agent, so a
      // new agent can never come out belonging to nothing. Applying them in a
      // second call meant one failed request left every switch silently off.
      payload.domainGroups = selectedGroups.map((group) => group._id);

      const response = await API.registeruser(payload);

      if (response.status) {
        // The agent exists either way, so a membership the server did not keep
        // is reported rather than thrown: the legacy booleans above still route
        // leads to them.
        const savedGroups = (response.user?.domainGroups || []).map(String);
        const missing = selectedGroups
          .filter((group) => !savedGroups.includes(String(group._id)))
          .map((group) => group.name);

        if (missing.length) {
          message.warning(`Agent created, but could not add them to: ${missing.join(", ")}`);
        }

        message.success("Team member added successfully!");
        this.formRef.current.resetFields();
        this.setState({
          visible: false,
          currentStep: 0,
          formValues: {},
          loading: false,
          selectedRole: "",
          selectedSubRole: "",
        });
        this.props.refreshTeamList();
      } else {
        message.error(response.message || "Failed to add team member.");
        this.setState({ loading: false });
      }
    } catch (err) {
      console.log("Please fix the errors before submitting.");
      this.setState({ loading: false });
    }
  };

  render() {
    const { currentStep, visible, loading, selectedRole, selectedSubRole } = this.state;

    return (
      <div>
        <Button type="primary" onClick={this.showDrawer}>
          <PlusOutlined size={20} /> Add Agent
        </Button>

        <Drawer title="Add Agent" width={500} onClose={this.closeDrawer} open={visible}>
          <Steps current={currentStep}>
            <Step title="Personal Info" />
            <Step title="Account Info" />
          </Steps>

          <Form
            ref={this.formRef}
            layout="vertical"
            style={{ marginTop: "21px" }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
              }
            }}
          >
            {currentStep === 0 && (
              <>
                <Form.Item name="firstName" label="First Name" rules={[{ required: true, message: "Enter valid first name" }]}>
                  <Input placeholder="Enter first name" onChange={(e) => this.handleNameChange("firstName", e)} />
                </Form.Item>

                <Form.Item name="lastName" label="Last Name" rules={[{ required: true, message: "Enter valid last name" }]}>
                  <Input placeholder="Enter last name" onChange={(e) => this.handleNameChange("lastName", e)} />
                </Form.Item>

                <Form.Item name="email" label="Email" rules={[{ required: true, type: "email", message: "Enter valid email" }]}>
                  <Input className="border-0 bg-white text-black" disabled placeholder="Auto generated email" />
                </Form.Item>

                <Form.Item name="role" label="Role" rules={[{ required: true, message: "Select role" }]}>
                  <Select
                    placeholder="Select role"
                    onChange={(value) => {
                      this.formRef.current.setFieldsValue({
                        role: value,
                        subRole: undefined,
                        ...this.clearedGroupFields(),
                      });
                      this.setState({ selectedRole: value, selectedSubRole: "" });
                    }}
                  >
                    <Option value="ADMIN">Admin</Option>
                    <Option value="SALES-AGENT">Sales Agent</Option>
                    <Option value="SERVICE-AGENT">Service Agent</Option>
                  </Select>
                </Form.Item>

                {selectedRole === "SALES-AGENT" && (
                  <Form.Item name="subRole" label="Sub Role" rules={[{ required: true, message: "Select sub role" }]}>
                    <Select
                      placeholder="Select sub role"
                      onChange={(value) => {
                        // A group belongs to one tier, so switching tier clears
                        // every group switch rather than carrying them across.
                        this.formRef.current.setFieldsValue({
                          subRole: value,
                          ...this.clearedGroupFields(),
                        });
                        this.setState({ selectedSubRole: value });
                      }}
                    >
                      <Option value="TIER-1">TIER-1</Option>
                      <Option value="TIER-2">TIER-2</Option>
                      <Option value="TIER-3">TIER-3</Option>
                    </Select>
                  </Form.Item>
                )}

                {selectedRole === "SALES-AGENT" && this.getTierGroups().map((group) => (
                  <Form.Item
                    key={group._id}
                    name={groupFieldName(group)}
                    label={group.name}
                    valuePropName="checked"
                    initialValue={false}
                  >
                    <Switch />
                  </Form.Item>
                ))}
              </>
            )}

            {currentStep === 1 && (
              <>
                <Form.Item name="extension" label="Extension" rules={[{ validator: this.validateExtension, required: true }]}>
                  <Input maxLength={3} placeholder="Enter extension" />
                </Form.Item>

                <Form.Item
                  name="directNumber"
                  label="Direct Number"
                  rules={[{ validator: this.validatePhoneNumber, required: true }]}
                >
                  <Input
                    maxLength={18}
                    placeholder="+1 (XXX) XXX-XXXX"
                    onChange={(e) => {
                      const formatted = this.formatPhoneNumber(e.target.value);
                      this.formRef.current.setFieldsValue({ directNumber: formatted });
                    }}
                  />
                </Form.Item>

                <Form.Item
                  name="userName"
                  label="User Name"
                  rules={[
                    { required: true, message: "Username is required" },
                    { validator: this.validateUsername },
                  ]}
                >
                  <Input
                    placeholder="Enter username"
                    onChange={(e) => {
                      const noSpaces = e.target.value.replace(/\s+/g, "");
                      this.formRef.current.setFieldsValue({ userName: noSpaces });
                    }}
                    disabled
                  />
                </Form.Item>
              </>
            )}

            <div style={{ marginTop: "20px", display: "flex", justifyContent: "flex-end", gap: "21px" }}>
              {currentStep > 0 && <Button onClick={this.prevStep}>Previous</Button>}
              {currentStep < 1 && <Button type="primary" onClick={this.nextStep}>Next</Button>}
              {currentStep === 1 && (
                <Button type="primary" loading={loading} disabled={loading} onClick={this.onFinish}>
                  Submit
                </Button>
              )}
            </div>
          </Form>
        </Drawer>
      </div>
    );
  }
}

/**
 * AddTeam is a class component, so the groups are read here and handed down as a
 * prop rather than through the hook.
 */
const AddTeamWithDomainGroups = (props) => {
  const { visibleGroups } = useDomainGroups();
  return <AddTeam {...props} domainGroups={visibleGroups} />;
};

export default AddTeamWithDomainGroups;
